import { useAlerts } from "components/Alerts/AlertProvider";
import {
  Issue,
  ReviewMode,
  SignatureType,
  SubmissionReviewerInput,
} from "generated-graphql/graphql";
import { useUpdateSubmission } from "hooks/useSubmissionMutations";
import { useCallback } from "react";

/** Plain config save, and the pre-signing save that gates on validation before opening BoldSign preparation. */
export function useReviewerPreparationSaveHandlers({
  updateSubmission,
  submissionId,
  signatureType,
  reviewRequired,
  peerReviewToPersist,
  reviewMode,
  reviewerInputs,
  alerts,
  revalidate,
  setPreparationDialogOpen,
}: {
  updateSubmission: ReturnType<typeof useUpdateSubmission>[0];
  submissionId: string;
  signatureType: SignatureType;
  reviewRequired: boolean;
  peerReviewToPersist: boolean;
  reviewMode: ReviewMode;
  reviewerInputs: SubmissionReviewerInput[];
  alerts: ReturnType<typeof useAlerts>;
  revalidate: () => Promise<Issue[]>;
  setPreparationDialogOpen: (next: boolean) => void;
}) {
  // Plain save without advancing the flow; skips the validation gate since
  // half-finished config is exactly what an intermittent save is for.
  const handleSaveConfig = useCallback(async () => {
    try {
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: reviewerInputs,
          },
        },
      });
      alerts.success("Changes saved.");
    } catch (error) {
      alerts.error("Failed to save changes.", error);
    }
  }, [
    updateSubmission,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewerInputs,
    alerts,
  ]);

  const handlePrepareForSigning = useCallback(async () => {
    const currentIssues = await revalidate();
    if (currentIssues.length > 0) {
      alerts.error("Resolve the validation issues before preparing the document for signing.");
      return;
    }
    try {
      // Persist the signature type + reviewer/signer config before opening the
      // BoldSign preparation dialog (which fetches its URL on mount).
      await updateSubmission({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: reviewerInputs,
          },
        },
      });
      setPreparationDialogOpen(true);
    } catch (error) {
      alerts.error("Failed to save before preparing for signing.", error);
    }
  }, [
    revalidate,
    updateSubmission,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewerInputs,
    alerts,
    setPreparationDialogOpen,
  ]);

  return { handleSaveConfig, handlePrepareForSigning };
}
